// QuoteEmbed.tsx — 1.21.00 Slice 2: compact bordered embed of a quoted post.
// Rendered on PostCard, HeroPostCard, and the Composer's non-editable preview.
//
// The whole embed is a real <a> (via react-router's Link) so PostCard's
// onCardClick (which ignores clicks inside `a, button, [role="menu"], .post-status`)
// never double-navigates or hijacks the click — this IS the navigation.
//
// No action row: a stale like/cheer/spread count baked into an embed snapshot
// would drift from the real post immediately, so the embed is display-only.

import type { MouseEvent } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import type { QuotedPost, QuotedPostUnavailable } from '@/core/models/post';
import { postPath } from '@/core/lib/postUrl';
import { Avatar, MetaRow } from '@/ui';
import { Icon } from '@/icons';

const Card = styled(Link)`
  display: block;
  margin-top: 8px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  text-decoration: none;
  color: inherit;
  transition: border-color 0.15s;


`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Name = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Handle = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

/* The handle and time sit apart with MetaRow's gap, measured at their own size. */
const HandleTime = styled(MetaRow)`
  font-size: ${({ theme }) => theme.fontSize.xs};
`;

const Time = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Body = styled.p`
  margin: 6px 0 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Thumb = styled.img`
  margin-top: 8px;
  width: 100%;
  max-height: 160px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.sm};
`;

// W12 (1.21.00 review): `hasQuote` was on the wire shape but never rendered —
// a quote-of-a-quote embed looked identical to a plain quoted post, with no
// hint that following it leads to another nested quote.
const NestedQuoteHint = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Unavailable = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  margin-top: 8px;
  border: 1px dashed ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  color: ${({ theme }) => theme.color.textMuted};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

function isUnavailable(q: QuotedPost | QuotedPostUnavailable): q is QuotedPostUnavailable {
  return 'unavailable' in q && q.unavailable === true;
}

export interface QuoteEmbedProps {
  quotedPost: QuotedPost | QuotedPostUnavailable;
  /** Stop the click from bubbling to an ancestor's own navigation handler. */
  onClick?: (e: MouseEvent) => void;
}

export function QuoteEmbed({ quotedPost, onClick }: QuoteEmbedProps) {
  if (isUnavailable(quotedPost)) {
    return (
      <Unavailable data-testid="quote-embed-unavailable">
        <Icon name="ban" size={16} />
        This post is no longer available.
      </Unavailable>
    );
  }
  const thumbUrl = quotedPost.images?.[0]?.url;
  return (
    <Card
      to={postPath(quotedPost)}
      onClick={onClick}
      aria-label={`Quoted post by ${quotedPost.name}: ${quotedPost.body.slice(0, 80)}`}
      data-testid="quote-embed"
    >
      <Row>
        <Avatar name={quotedPost.name} size={20} avatarKey={quotedPost.av} />
        <Name>{quotedPost.name}</Name>
        <HandleTime>
          <Handle>{quotedPost.handle}</Handle>
          <Time>{quotedPost.time}</Time>
        </HandleTime>
      </Row>
      {quotedPost.body && <Body>{quotedPost.body}</Body>}
      {thumbUrl && <Thumb src={thumbUrl} alt="" />}
      {quotedPost.hasQuote && (
        <NestedQuoteHint data-testid="quote-embed-has-quote">
          <Icon name="quote" size={12} />
          Contains a quote
        </NestedQuoteHint>
      )}
    </Card>
  );
}
